import type { FieldKind } from "@storyboard/dispatch-view/fieldKinds";
import type { DispatchColor } from "@storyboard/dispatch-view/view";
import type { DispatchTableRow } from "@/hooks/dispatch/dispatchTableRow";

/** One value a `values` control can offer: the raw value, what to call it, its color, and how many rows carry it. */
export type ValueOption = { value: string; label: string; color?: DispatchColor; count: number };

/** The two options a boolean field offers. */
const BOOLEAN_OPTIONS: readonly { value: string; label: string }[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

/**
 * The options a `values` control offers for a field, with row counts.
 *
 * An enum field lists every value the registry knows, in registry order, so the chips never shift as the data changes;
 * a boolean offers its two states by the names the registry gives them, else Yes and No; every other kind lists the
 * distinct values the rows carry, sorted. Multi-value fields count each entry. A blank or whitespace entry is no
 * value; {@link blankCount} counts those rows.
 */
export function valueOptions(rows: readonly DispatchTableRow[], key: string, kind: FieldKind): ValueOption[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const value = row.record[key];
    const entries = Array.isArray(value) ? value : value ? [value] : [];
    for (const entry of entries) {
      if (entry.trim() !== "") counts.set(entry, (counts.get(entry) ?? 0) + 1);
    }
  }
  if (kind.valueType === "enum" && kind.values) {
    return kind.values.map((value) => ({ ...value, count: counts.get(value.value) ?? 0 }));
  }
  if (kind.valueType === "boolean") {
    return (kind.values ?? BOOLEAN_OPTIONS).map((option) => ({ ...option, count: counts.get(option.value) ?? 0 }));
  }
  return [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map(([value, count]) => ({ value, label: value, count }));
}

/**
 * How many rows have no value in a field: missing, empty, or whitespace, and for a multi-value field no entry that
 * isn't. The rows an `isEmpty` condition keeps, so the (blank) choice can show its count.
 */
export function blankCount(rows: readonly DispatchTableRow[], key: string): number {
  let count = 0;
  for (const row of rows) {
    const value = row.record[key] ?? "";
    const entries = Array.isArray(value) ? value : [value];
    if (entries.every((entry) => entry.trim() === "")) count++;
  }
  return count;
}
